import { isExistingSiteEntryMode } from 'config/entry-mode';
import { ActionId, StepId } from 'config/steps';
import { useAppStore } from 'stores/appStore';
import { SelectCategoryStep } from 'steps/select-category/SelectCategoryStep';
import { createFlow } from '@wix/gg-components/orchestrator';
import { StepParams, StepRenderPart } from 'types/steps';
import { SelectCategoryStepGallery } from 'steps/select-category/components/SelectCategoryStepGallery';
import { SelectProductsStep } from 'steps/select-products/SelectProductsStep';
import { SelectProductsStepPreview } from 'steps/select-products/components/SelectProductsStepPreview';
import { actions } from 'api/actions';
import { redirects } from 'api/redirects';
import { SelectImageStep } from 'steps/select-image/SelectImageStep';
import { StoreSetupStep } from 'steps/store-setup/StoreSetupStep';
import { SelectNameStep } from 'steps/select-name/SelectNameStep';
import { SelectImageStepPreview } from 'steps/select-image/components/SelectImageStepPreview';
import { LandingPageStep } from 'steps/landing-page/LandingPageStep';
import { SelectNameStepPreview } from 'steps/select-name/components/SelectNameStepPreview';
import { StoreSetupStepPreview } from 'steps/store-setup/components/StoreSetupStepPreview';
import { StoreReadyStep } from 'steps/store-ready/StoreReadyStep';
import { StoreReadyStepMobile } from 'steps/store-ready/components/StoreReadyStepMobile';
import { ExistingSiteStoreReadyMain } from 'steps/existing-site-store-ready/ExistingSiteStoreReadyMain';
import { ExistingSiteStoreReadySide } from 'steps/existing-site-store-ready/ExistingSiteStoreReadySide';
import { useIsMobile } from 'hooks/useIsMobile';
import { withRetry } from 'utils/with-retry';
import { PrintfulConnectionStep } from 'steps/printful-connection/PrintfulConnectionStep';

export const useFlowBuilder = () => {
  const isExistingSiteMode = isExistingSiteEntryMode(useAppStore.getState().entryMode);
  const isMobile = useIsMobile();

  const flow = createFlow<StepParams, StepRenderPart>();
  flow.step(StepId.LandingPage, LandingPageStep, { hideProgress: true });

  if (!isExistingSiteMode) {
    flow.step(
      StepId.SelectCategory,
      { main: SelectCategoryStep, side: SelectCategoryStepGallery },
      { sideRatio: 'fill', disableBack: true },
    );
  }

  flow.step(StepId.SelectProducts, { main: SelectProductsStep, side: SelectProductsStepPreview });
  flow.action(ActionId.RedirectToLogin, redirects.redirectToLogin);

  flow.action(ActionId.CreateMetaSite, async () => {
    if (isExistingSiteEntryMode(useAppStore.getState().entryMode)) {
      return;
    }

    await actions.createMetaSite();
    await withRetry(actions.installSiteApps, 4, 2 * 1000);
  });

  if (isExistingSiteMode) {
    flow.step(StepId.SelectImage, { main: SelectImageStep, side: SelectImageStepPreview }, { sideRatio: 'fill' });
    flow.action(ActionId.CaptureStoresInstalledAtEntry, actions.captureStoresInstalledAtEntry);
    flow.action(ActionId.RedirectToConnectPrintful, redirects.redirectToConnectPrintful);
    flow.step(StepId.PrintfulConnection, PrintfulConnectionStep, {}, () => !!window.__IS_PRINTFUL_CONNECTED__);
  } else {
    flow.action(ActionId.RedirectToConnectPrintful, redirects.redirectToConnectPrintful);
    flow.step(StepId.PrintfulConnection, PrintfulConnectionStep, {}, () => !!window.__IS_PRINTFUL_CONNECTED__);
    flow.step(StepId.SelectImage, { main: SelectImageStep, side: SelectImageStepPreview }, { sideRatio: 'fill' });
  }

  if (!isExistingSiteMode) {
    flow.step(StepId.SelectName, { main: SelectNameStep, side: SelectNameStepPreview }, { sideRatio: 'fill' });
  }

  flow.step(
    StepId.StoreSetup,
    { main: StoreSetupStep, side: StoreSetupStepPreview },
    { hideProgress: true, sideRatio: 'fill' },
  );

  if (isExistingSiteMode) {
    flow.step(
      StepId.StoreReady,
      { main: ExistingSiteStoreReadyMain, side: ExistingSiteStoreReadySide },
      { hideProgress: true, sideRatio: 'half' },
    );
  } else if (isMobile) {
    flow.step(StepId.StoreReady, { main: StoreReadyStepMobile }, { hideProgress: true });
  } else {
    flow.step(StepId.StoreReady, { side: StoreReadyStep }, { hideProgress: true, sideRatio: 'full' });
  }

  if (!isExistingSiteMode) {
    flow.action(ActionId.RedirectToUpgradePlan, redirects.redirectToUpgradePlan);
  }
  return flow.build();
};
